import { PropsWithChildren, ReactNode, useState } from "react";
import React from "react";
import clsx from "clsx";
import { format } from "date-fns";

import { AppointmentSummaryProps } from "./AppointmentSummary";

import { OfficeSummary } from "~/components/Appointment/DesktopAppointmentSummary";
import { EmployeeSummary } from "~/components/Appointment/EmployeeSummary";
import { MeetingTypeSummary } from "~/components/Appointment/MeetingTypeSummary";
import { SubjectSummary } from "~/components/Appointment/SubjectSummary";
import { Button } from "~/components/Button";
import { ChevronIcon, DIRECTION } from "~/components/Icons/ChevronIcon";
import { Text } from "~/components/Text";
import { useTimeZone } from "~/utils/date";
import { getFullName } from "~/utils/getFullName";
import { useLocalisedMonth, useLocalisedTime } from "~/utils/timetable.utils";

export function MobileAppointmentSummary({
  employee,
  isMobileCollapsible = false,
  location,
  meetingType,
  office,
  possibility,
  subject,
}: AppointmentSummaryProps) {
  const employeeName = getFullName(employee);

  const nbItems = [subject?.title, office?.title, employeeName].filter(Boolean).length;
  const isEmpty = nbItems === 0;

  if (isEmpty) {
    return null;
  }

  return (
    <div
      className={clsx(
        "grid",
        "p-sm pb-0 sm:p-md sm:pb-0",
        "ring-1 ring-inset ring-gray-200",
        "bg-white",
        "rounded-2xl",
        "shadow-sm",
      )}
      data-component="SummaryMobile"
    >
      <SummaryCollapse isMobileCollapsible={isMobileCollapsible}>
        {subject?.title && (
          <div className="grid grid-cols-3 items-center gap-xs pb-xs sm:grid-cols-4">
            <div className="col-span-2 [word-break:break-word] sm:col-span-3">
              <SubjectSummary externalId={subject.externalId} title={subject.title} />
            </div>
            {possibility?.end && possibility?.start && (
              <TimetableSummaryMobile
                end={possibility.end}
                officeTimeZone={office?.timeZone ?? null}
                start={possibility.start}
              />
            )}
          </div>
        )}

        {meetingType && (
          <MeetingTypeSummary
            employeeName={employeeName}
            locationAddress={location?.address ?? null}
            meetingType={meetingType}
            office={office}
          />
        )}

        {employeeName && <EmployeeSummary {...employee} size="small" showLabel />}
        {meetingType !== "office" && office && <OfficeSummary office={office} />}
      </SummaryCollapse>
    </div>
  );
}

interface TimetableSummaryProps {
  start: string;
  end: string;
  officeTimeZone: string | null;
}

function TimetableSummaryMobile({ end, officeTimeZone, start }: TimetableSummaryProps) {
  const timeZone = useTimeZone({ officeTimeZone });

  const month = useLocalisedMonth({ start, timeZone });
  const time = useLocalisedTime({ start, end, timeZone });
  if (!start || !month || !time) {
    return null;
  }

  const startDate = new Date(start);

  return (
    <div
      className={clsx("grid justify-items-center gap-3xs", "p-xs", "rounded-2xl border")}
      data-component="TimetableSummaryMobile"
    >
      <div className="uppercase">{month}</div>
      {start && <div className="text-2xl font-bold">{format(startDate, "d")}</div>}
      <div className="whitespace-nowrap text-xl font-bold text-gray-500">{time.start}</div>
      {/* <span className="text-sm italic" data-component="TimetableSummaryTimeZone">
        {timeZone}
      </span> */}
    </div>
  );
}

function SummaryCollapse(props: PropsWithChildren<{ isMobileCollapsible: boolean }>) {
  const [maxChildren] = useState(props.isMobileCollapsible ? 2 : 10);
  const [isExpanded, setIsExpanded] = useState(!props.isMobileCollapsible);

  const children = React.Children.toArray(props.children) as ReactNode[];
  const renderedChildren = children.filter(Boolean).slice(0, maxChildren);

  return (
    <div className="grid gap-xs">
      <div className="grid gap-sm" data-component="SummaryCollapse">
        {isExpanded ? props.children : renderedChildren}
      </div>
      {children.length > renderedChildren.length ? (
        <Button
          className="flex items-center justify-center gap-xs py-sm text-sm text-gray-500"
          onPress={() => setIsExpanded((o) => !o)}
        >
          <ChevronIcon
            className="text-gray-900"
            direction={isExpanded ? DIRECTION.UP : DIRECTION.DOWN}
          />
          <span>
            <Text i18nKey={isExpanded ? "summary.show_less" : "summary.show_more"} />
          </span>
        </Button>
      ) : (
        // Placeholder
        <div className="h-sm" />
      )}
    </div>
  );
}
